import { Text } from '@kula-ai/components';
import { useDepartmentsList } from './useDepartmentsList';
import { DepartmentPathTrail } from '@/components/formBuilder/SelectComponents/DepartmentSingleValue';
import type { DepartmentNode } from '@/components/formBuilder/SelectComponents/departmentPath';

interface DepartmentPathProps {
  /**
   * The record's department; only `id` and `name` are needed. Without an id
   * it is found by name when the name is unique, else shown on its own.
   */
  department?: { id?: DepartmentNode['id'] | null; name: string } | null;
}

/**
 * A department shown with its hierarchy, read-only — the same breadcrumb the
 * job's department select shows. The ancestors come from the shared
 * departments list (cached; the job and requisition forms read it too), so
 * until it loads the department's own name is shown.
 */
// By id when the record has one. Some lists (search results, AI answers)
// only carry the name: use the department with that name if exactly one has
// it, since a shared name can't say which branch it sits on.
const findDepartment = (
  departments: DepartmentNode[],
  department: { id?: DepartmentNode['id'] | null; name: string }
) => {
  if (department.id !== null && department.id !== undefined) {
    return departments.find((item) => item.id === department.id);
  }
  const named = departments.filter((item) => item.name === department.name);
  return named.length === 1 ? named[0] : undefined;
};

export const DepartmentPath = ({ department }: DepartmentPathProps) => {
  const departments = useDepartmentsList();

  if (!department) return <Text variant="span">-</Text>;

  const node = findDepartment(departments, department) ?? {
    id: department.id ?? department.name,
    name: department.name,
  };

  return (
    <DepartmentPathTrail department={node} allDepartments={departments} wrap />
  );
};
